import { Instant, LocalDateTime } from "@js-joda/core";
import assertNever from "assert-never";
import { DateFormatterOptions } from "globalize";
import * as React from "react";
import * as Ons from "react-onsenui";
import { deepEqual } from "../deep-equal";
import { formatPriceUSD } from "../format";
import { DistanceRequest } from "../GeoService";
import { isLoading, Loading, loading } from "../Loading";
import * as messages from "../messages";
import {
  Geo,
  PatientAvailableTaskBroadcastDetails,
  PatientOnboardingTaskBroadcastDetails,
  PatientTaskBroadcastDetails,
} from "../messages";
import { CaregiverStatus } from "../messages/CaregiverStatus";
import {
  AgencyId,
  CaregiverCertification,
  OnboardingVisitId,
  PatientTaskInstanceId,
  VisitBroadcastId,
} from "../messages/ObjectIds";
import { AddressComponents } from "../messages/Visit";
import { STRINGS } from "../strings";
import { AgencyWithChatOnlyDetails } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit, VisitId } from "../system/AppState/AppVisitsState";
import { globalize, translate } from "../translate";
import { getTotalFlexibleVisitHours } from "../utils/flexibleVisitUtils";
import { HashMap } from "../utils/HashMap";
import { List } from "../utils/List";
import { Ord } from "../utils/Ord";
import { comparable, reverseOrd } from "../utils/OrdTuple";
import { undefinedToNull } from "../utils/undefinedToNull";
import AvailableTaskListItem from "./AvailableTaskListItem/AvailableTaskListItem";
import { calcGeoDistance, DistanceLabel } from "./DistanceLabel";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import EngagementLabel from "./Generic/EngagementLabel";
import { isDefined } from "./Generic/IsDefined";
import PatientAvatar from "./PatientAvatar/PatientAvatar";
import ScreenPlaceholder from "./ScreenPlaceholder";
import { metersToMilesRounded } from "./VisitListComponents";
import { SortState } from "./VisitsScreen";

export const visitStatus: { [key in VisitStatus]: key } = {
  VIEWED: "VIEWED",
  NEW: "NEW",
  PENDING_CONFIRMATION: "PENDING_CONFIRMATION",
  REQUESTED: "REQUESTED",
};

export type VisitStatus = "VIEWED" | "REQUESTED" | "NEW" | "PENDING_CONFIRMATION";
export type TaskStatus = "VIEWED" | "NEW";
export type VisitStatusColor = "yellow" | "green" | "slate" | "blue";

export const ICON_COLORS_AMOUNT: number = 14;

export interface AvailableVisitsAndTasksListProps {
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>;
  caregiver: AppCaregiver;
  availableVisits: List<Visit>;
  availableTaskBroadcasts: List<messages.PatientTaskBroadcastDetails>;
  sortBy: SortState;
  getAnomalies: () => Promise<string[]>;
  onVisitClick: (visitBroadcastId: VisitBroadcastId) => void;
  onOnboardingVisitClick: (onboardingVisitId: OnboardingVisitId) => void;
  onCaseSettingsNavigationClick: () => void;
  onAvailableVisitCardViewed: (visitIds: VisitBroadcastId[]) => void;
  onVisitMoreDetailsViewed: (visitId: VisitBroadcastId) => void;
  onAvailableTaskClick: (taskBroadcastId: PatientTaskInstanceId) => void;
  onPendingConfirmationVisitClick: (visitId: VisitBroadcastId, isVisitConfirmed: boolean) => void;
}

interface DistanceData {
  distance: number;
  distanceRequest: DistanceRequest;
}

interface AvailableVisitsAndTasksListState {
  visitsDistance: Map<VisitBroadcastId | OnboardingVisitId, DistanceData>;
  getDistances: () => void;
  anomalies: Loading | string[];
  getAnomalies: () => void;
}

export class AvailableVisitAndTasksList extends React.Component<
  AvailableVisitsAndTasksListProps,
  AvailableVisitsAndTasksListState
> {
  private loadingAnomalies: boolean = false;

  shouldComponentUpdate(
    nextProps: AvailableVisitsAndTasksListProps,
    nextState: AvailableVisitsAndTasksListState
  ) {
    return (
      this.props.agencies !== nextProps.agencies ||
      this.props.caregiver !== nextProps.caregiver ||
      this.props.availableVisits !== nextProps.availableVisits ||
      this.props.sortBy !== nextProps.sortBy ||
      this.props.availableTaskBroadcasts !== nextProps.availableTaskBroadcasts ||
      !deepEqual(this.state.visitsDistance, nextState.visitsDistance)
    );
  }
  constructor(props: AvailableVisitsAndTasksListProps) {
    super(props);

    this.state = {
      visitsDistance: new Map<VisitBroadcastId, DistanceData>(),
      getDistances: this.getDistances.bind(this),
      anomalies: loading(),
      getAnomalies: this.getAnomalies.bind(this),
    };
  }

  componentDidMount(): void {
    this.emitLastViewed();
  }

  componentDidUpdate(_prevProps: AvailableVisitsAndTasksListProps): void {
    this.emitLastViewed();
  }

  emitLastViewed(): void {
    const notViewedVisits = this.props.availableVisits
      .filter(
        (visit) => visit.type === "Available" && !visit.caregiverVisitEngagementDetails.viewed
      )
      .map((visit) => visit as Visit.Available);

    for (const visit of notViewedVisits) {
      visit.caregiverVisitEngagementDetails.viewed = true;
    }

    if (notViewedVisits.length() > 0) {
      this.props.onAvailableVisitCardViewed(notViewedVisits.map((visit) => visit.id).toArray());
    }
  }

  handleVisitMoreDetailsClick = (visit: Visit) => {
    switch (visit.type) {
      case "Onboarding":
        this.props.onOnboardingVisitClick(visit.id);
        break;
      case "Available": {
        const status = getVisitStatus(visit);
        if (status === "PENDING_CONFIRMATION") {
          const isVisitConfirmed =
            visit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt !== null &&
            visit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt !== undefined;
          this.props.onPendingConfirmationVisitClick(visit.id, isVisitConfirmed);
        } else {
          this.props.onVisitMoreDetailsViewed(visit.id);
          this.props.onVisitClick(visit.id);
        }
        break;
      }
      default:
        assertNever(visit);
    }
  };

  static getDerivedStateFromProps(
    _props: AvailableVisitsAndTasksListProps,
    state: AvailableVisitsAndTasksListState
  ) {
    state.getDistances();
    state.getAnomalies();

    return null;
  }

  getDistances() {
    let hasChanged: boolean = false;
    const newDistances: Map<VisitBroadcastId | OnboardingVisitId, DistanceData> =
      this.state.visitsDistance;

    const origin: Geo = this.props.caregiver.addressGeoLocation;

    for (const visit of this.props.availableVisits) {
      const newRequest: DistanceRequest = {
        origin: origin,
        destination: visit.publicVisitBroadcastDetails.location.location,
      };

      const oldDistanceData: DistanceData | undefined = this.state.visitsDistance.get(visit.id);

      // If the new request is different
      if (
        oldDistanceData === undefined ||
        !deepEqual(oldDistanceData.distanceRequest, newRequest)
      ) {
        const distanceResult: number =
          calcGeoDistance(origin, visit.publicVisitBroadcastDetails.location.location, "K") * 1000;

        newDistances.set(visit.id, {
          distance: distanceResult,
          distanceRequest: newRequest,
        });

        hasChanged = true;
      }
    }

    // Cleanup deleted visits
    newDistances.forEach((_value: DistanceData, key: VisitId) => {
      // If visit doesn't exist any more
      if (
        this.props.availableVisits.none((visit) => visit.publicVisitBroadcastDetails.id === key)
      ) {
        newDistances.delete(key);
      }
    });

    if (hasChanged) {
      this.setState({ visitsDistance: newDistances });
    }
  }

  getVisitById(visitBroadcastId: VisitBroadcastId) {
    return undefinedToNull(this.props.availableVisits.find((x) => x.id === visitBroadcastId));
  }

  shouldLoading() {
    switch (this.props.sortBy) {
      case "DISTANCE":
        return this.state.visitsDistance.size !== this.props.availableVisits.length();
      case "START_DATE":
      case "POSTED_DATE":
      case "PRICE":
      case "DEFAULT":
        return false;
      default:
        return assertNever(this.props.sortBy);
    }
  }

  handleCaseSettingsFix() {
    this.setState({ anomalies: loading() });
    this.props.onCaseSettingsNavigationClick();
  }

  getAnomalies() {
    if (
      !this.loadingAnomalies &&
      this.props.availableVisits.length() + this.props.availableTaskBroadcasts.length() === 0 &&
      isLoading(this.state.anomalies)
    ) {
      this.loadingAnomalies = true;

      this.props.getAnomalies().then((data) => {
        this.setState({ anomalies: data });
        this.forceUpdate();

        this.loadingAnomalies = false;
      });
    }
  }

  render() {
    return (
      <div id="availableVisitsListTop">
        {this.props.availableVisits.length() + this.props.availableTaskBroadcasts.length() === 0 ? (
          !isLoading(this.state.anomalies) && this.state.anomalies.length !== 0 ? (
            <div className="VisitsScreen__caseSettingsProblem">
              <div style={{ color: "#444" }} className="VisitsScreen__placeholderText">
                {translate(STRINGS.PROBLEM_WITH_CASE_SETTINGS)}
              </div>
              <div style={{ marginLeft: "auto", marginRight: "auto" }}>
                <AppButton onClick={() => this.handleCaseSettingsFix()}>
                  {translate(STRINGS.FIX_SETTINGS)}
                </AppButton>
              </div>
            </div>
          ) : (
            <ScreenPlaceholder
              headline={translate(STRINGS.YOU_DONT_HAVE_ANY_CASES_YET)}
              subheadline={translate(STRINGS.STAY_TUNED_FOR_MORE_CASES_OR_TAP_TO_CAHNGE_SETTINGS)}
              illustration="NoAvailableCases"
              buttonText={translate(STRINGS.CHANGE_SETTINGS)}
              onClick={this.props.onCaseSettingsNavigationClick}
            />
          )
        ) : this.shouldLoading() ? (
          <Ons.ProgressCircular className="AvailableVisitsList__loading" indeterminate={true} />
        ) : (
          <div className="AvailableVisitsList__Items">
            {sortVisitsAndTasks(
              this.props.sortBy,
              this.props.agencies,
              this.props.availableVisits,
              this.props.availableTaskBroadcasts,
              this.props.caregiver.addressGeoLocation
            )
              .zipWithIndex()
              .map(([visitOrTask, index]) => {
                switch (visitOrTask.taskOrVisit) {
                  case "Visit":
                    return (
                      <AvailableVisitListItem
                        key={`visit-${VisitBroadcastId.unwrap(visitOrTask.id)}`}
                        index={index}
                        caregiver={this.props.caregiver}
                        visit={visitOrTask}
                        handleVisitMoreDetailsClick={this.handleVisitMoreDetailsClick}
                      />
                    );
                  case "OnboardingVisit":
                    return (
                      <AvailableVisitListItem
                        key={`onboarding-${OnboardingVisitId.unwrap(visitOrTask.id)}`}
                        index={index}
                        caregiver={this.props.caregiver}
                        visit={visitOrTask}
                        handleVisitMoreDetailsClick={this.handleVisitMoreDetailsClick}
                      />
                    );
                  case "Task":
                    return (
                      <AvailableTaskListItem
                        key={`task-${PatientTaskInstanceId.unwrap(visitOrTask.taskInstanceId)}`}
                        index={index}
                        caregiver={this.props.caregiver}
                        task={visitOrTask}
                        handleAvailableTaskClick={this.props.onAvailableTaskClick}
                      />
                    );
                  default:
                    return null;
                }
              })}
          </div>
        )}
      </div>
    );
  }
}

type AvailableTaskOrVisitType =
  | AvailableTaskOrVisitType.Task
  | AvailableTaskOrVisitType.Visit1
  | AvailableTaskOrVisitType.OnboardingVisit
  | AvailableTaskOrVisitType.OnboardingTask;
declare namespace AvailableTaskOrVisitType {
  export interface Visit1 extends Visit.Available, BaseDetails {
    taskOrVisit: "Visit";
  }
  export interface OnboardingVisit extends Visit.Onboarding, BaseDetails {
    taskOrVisit: "OnboardingVisit";
  }
  export interface Task extends PatientAvailableTaskBroadcastDetails, BaseDetails {
    taskOrVisit: "Task";
  }
  export interface OnboardingTask extends PatientOnboardingTaskBroadcastDetails, BaseDetails {
    taskOrVisit: "OnboardingTask";
  }

  interface BaseDetails {
    sortingVars: {
      postedDate: Instant;
      startDate: LocalDateTime;
      distance: DistanceData | undefined;
      price: number | null;
    };
  }
}

function visitsToUnionType<T extends Visit>(
  visits: List<T>,
  origin: Geo
): List<AvailableTaskOrVisitType> {
  const arr: AvailableTaskOrVisitType[] = [];
  for (const visit of visits) {
    const distanceResult: number =
      calcGeoDistance(origin, visit.publicVisitBroadcastDetails.location.location, "K") * 1000;
    const distanceData: DistanceData = {
      distance: distanceResult,
      distanceRequest: {
        destination: visit.publicVisitBroadcastDetails.location.location,
        origin: origin,
      },
    };

    switch (visit.type) {
      case "Onboarding":
        arr.push({
          taskOrVisit: "OnboardingVisit",
          ...visit,
          sortingVars: {
            distance: distanceData,
            postedDate: visit.publicVisitBroadcastDetails.createdAt,
            price: visit.publicVisitBroadcastDetails.price,
            startDate: visit.publicVisitBroadcastDetails.startTimeLocal,
          },
        });
        break;
      case "Available":
        arr.push({
          taskOrVisit: "Visit",
          ...visit,
          sortingVars: {
            distance: distanceData,
            postedDate: visit.publicVisitBroadcastDetails.createdAt,
            price: visit.publicVisitBroadcastDetails.price,
            startDate: visit.publicVisitBroadcastDetails.startTimeLocal,
          },
        });
        break;
      default:
        assertNever(visit);
    }
  }

  return List.fromArray(arr);
}

function tasksToUnionType(
  tasks: List<PatientTaskBroadcastDetails>,
  origin: Geo
): List<AvailableTaskOrVisitType> {
  const arr: AvailableTaskOrVisitType[] = [];
  for (const task of tasks) {
    let distanceData: DistanceData | undefined;
    if (task.patientAddressComponents !== null) {
      const distanceResult =
        calcGeoDistance(origin, task.patientAddressComponents.location, "K") * 1000;
      distanceData = {
        distance: distanceResult,
        distanceRequest: {
          destination: task.patientAddressComponents.location,
          origin: origin,
        },
      };
    }

    const sortingVars = {
      distance: distanceData,
      postedDate: task.createdAt,
      price: null,
      startDate: task.startDate.atStartOfDay(),
    };

    switch (task.type) {
      case "Available":
        arr.push({
          taskOrVisit: "Task",
          ...task,
          sortingVars,
        });
        break;

      case "Onboarding":
        arr.push({
          taskOrVisit: "OnboardingTask",
          ...task,
          sortingVars,
        });
    }
  }

  return List.fromArray(arr);
}

function sortVisitsAndTasks<T extends Visit>(
  sorting: SortState,
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>,
  visits: List<T>,
  tasks: List<PatientTaskBroadcastDetails>,
  caregiverOrigin: Geo
): List<AvailableTaskOrVisitType> {
  function onHoldSort(visitOrTask: AvailableTaskOrVisitType) {
    // Agencies that are "On Hold" should come after other agencies
    let agencyId: AgencyId;
    switch (visitOrTask.taskOrVisit) {
      case "Task":
      case "OnboardingTask":
        agencyId = visitOrTask.agency.id;
        break;
      case "Visit":
      case "OnboardingVisit":
        agencyId = visitOrTask.publicVisitBroadcastDetails.agency.id;
        break;
      default:
        assertNever(visitOrTask);
    }
    return isNoneActiveAgency(agencies, agencyId) ? 2 : 1;
  }

  function sortingOrd(visitOrTask: AvailableTaskOrVisitType): Ord {
    switch (sorting) {
      case "POSTED_DATE": {
        return reverseOrd(visitOrTask.sortingVars.postedDate);
      }
      case "START_DATE": {
        return visitOrTask.sortingVars.startDate;
      }
      case "DISTANCE": {
        const distanceData = visitOrTask.sortingVars.distance;
        return distanceData !== undefined ? distanceData.distance : 0;
      }
      case "PRICE": {
        const price = visitOrTask.sortingVars.price;
        return reverseOrd(price !== null ? price : 0);
      }
      case "DEFAULT": {
        return 0;
      }
      default:
        return assertNever(sorting);
    }
  }

  const availableTaskAndVisits: List<AvailableTaskOrVisitType> = tasksToUnionType(
    tasks,
    caregiverOrigin
  ).concat(visitsToUnionType(visits, caregiverOrigin));

  return availableTaskAndVisits.sortBy((val) => {
    switch (val.taskOrVisit) {
      case "Visit":
        return comparable(onHoldSort(val), sortingOrd(val));
      case "Task":
      case "OnboardingTask":
        return comparable(onHoldSort(val), sortingOrd(val));
      case "OnboardingVisit":
        return comparable(onHoldSort(val), sortingOrd(val));
      default:
        assertNever(val);
    }
  });
}

function isNoneActiveAgency(
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>,
  agencyId: AgencyId
): boolean {
  const activeStatuses: CaregiverStatus[] = [CaregiverStatus.ACTIVE, CaregiverStatus.PENDING];
  const agency = agencies.get(agencyId);
  if (agency === undefined) {
    return false;
  }
  return !activeStatuses.includes(agency.status);
}

interface AvailableVisitListItemProps {
  visit: Visit;
  handleVisitMoreDetailsClick: (visit: Visit) => void;
  caregiver: AppCaregiver;
  index: number;
}

class AvailableVisitListItem extends React.PureComponent<AvailableVisitListItemProps> {
  renderVisitInfo(visit: Visit) {
    return this.renderAvailable(visit);
  }

  renderAvailable(visit: Visit) {
    return (
      <div className="AvailableVisitsList__visitMilesStatusSection">
        <div className="AvailableVisitsList__visitMilesLabel">
          {!isLoading(this.props.caregiver) ? (
            <DistanceLabel
              distanceRequest={{
                origin: this.props.caregiver.addressGeoLocation,
                destination: visit.publicVisitBroadcastDetails.location.location,
              }}
              formatter={metersToMilesRounded}
            />
          ) : undefined}
          <span className="AvailableVisitsList__visitMilesLabelText">
            {translate(STRINGS.MILES_SHORT)}
          </span>
        </div>
        <div className="AvailableVisitsList__visitPrice">
          {visit.publicVisitBroadcastDetails.price !== null
            ? formatPriceUSD(visit.publicVisitBroadcastDetails.price)
            : ""}
        </div>
        <EngagementLabel
          text={getStatusText(getVisitStatus(visit))}
          type={getStatusType(getVisitStatus(visit))}
          className={
            "AvailableVisitsList__visitStatusPill AvailableVisitsList__visitStatusPill" +
            getVisitStatus(visit)
          }
        />
      </div>
    );
  }

  render() {
    const visit = this.props.visit;
    const startTimeYear = visit.publicVisitBroadcastDetails.startTimeLocal.year();
    const endTimeYear = visit.publicVisitBroadcastDetails.endTimeLocal.year();
    const isVisitDurationInSingleYear = startTimeYear === endTimeYear;
    const dateFormat: DateFormatterOptions = isVisitDurationInSingleYear
      ? { skeleton: "MMMd" }
      : { date: "short" };
    const formattedStartTime = globalize().formatLocalDateTime(
      visit.publicVisitBroadcastDetails.startTimeLocal,
      dateFormat
    );
    const formattedEndTime = globalize().formatLocalDateTime(
      visit.publicVisitBroadcastDetails.endTimeLocal,
      dateFormat
    );
    const hideVisitEndDate =
      formattedEndTime === formattedStartTime ||
      this.props.visit.publicVisitBroadcastDetails.hideEndDateFromCaregiver;

    return (
      <AppCard
        className="AvailableVisitsListItem"
        style={{ animationDelay: `${Math.min(this.props.index * 50, 400)}ms` }}
        onClick={() => this.props.handleVisitMoreDetailsClick(visit)}
      >
        <AppCardContent>
          <PatientAvatar
            initials={visit.publicVisitBroadcastDetails.patientInitials}
            className="AvailableVisitsList__visitIcon"
          />
          <div className="AvailableVisitsList__visitDetails">
            <div className="AvailableVisitsList__visitDetailsDate">
              <div className="AvailableVisitsList__visitDetailsStartDate">
                <span>{formattedStartTime}</span>
                {hideVisitEndDate ? "" : <span> {" - " + formattedEndTime}</span>}
              </div>
            </div>
            <div className="AvailableVisitsList__visitDetailsLocation">
              {getVisitDisplayLocation(visit.publicVisitBroadcastDetails.location)}
            </div>
            <div className="AvailableVisitsList__visitDetailsType">
              <span>{getVisitTypeText(getVisitType(visit))}</span>
              {visit.publicVisitBroadcastDetails.patientGender !== null ? (
                <span>
                  {visit.publicVisitBroadcastDetails.patientGender === "M"
                    ? translate(STRINGS.MALE)
                    : translate(STRINGS.FEMALE)}
                </span>
              ) : (
                ""
              )}
              <span>
                {visit.publicVisitBroadcastDetails.certifications
                  .map(CaregiverCertification.unwrap)
                  .join(", ")}
              </span>
            </div>
            <div className="AvailableVisitsList__visitDuration">{getVisitDuration(visit)}</div>
          </div>
          {this.renderVisitInfo(visit)}
        </AppCardContent>
      </AppCard>
    );
  }
}

function getVisitDisplayLocation(address: AddressComponents): string {
  const toReturn = [];

  if (address.sublocalityLevel1 !== null && address.sublocalityLevel1 !== undefined) {
    toReturn.push(address.sublocalityLevel1);
  }
  if (address.neighborhood !== null && address.neighborhood !== undefined) {
    toReturn.push(address.neighborhood);
  }
  if (address.locality !== null && address.locality !== undefined) {
    toReturn.push(address.locality);
  }

  return toReturn.join(", ");
}

export function getVisitTypeText(visitType: Visit["publicVisitBroadcastDetails"]["type"]): string {
  switch (visitType) {
    case "Singles":
      return translate(STRINGS.SINGLE_VISIT_TYPE);
    case "Pattern":
      return translate(STRINGS.PERMANENT_VISIT_TYPE);
    case "FlexiblePattern":
      return translate(STRINGS.FLEXIBLE_VISIT_TYPE);
    default:
      assertNever(visitType);
  }
}

export function getVisitType(visit: Visit) {
  return visit.publicVisitBroadcastDetails.type;
}

export function getVisitDuration(visit: Pick<Visit, "publicVisitBroadcastDetails">): string {
  switch (visit.publicVisitBroadcastDetails.type) {
    case "Pattern": {
      const workDaysPerWeek = Object.keys(
        visit.publicVisitBroadcastDetails.weeklyPattern.daysOfWeek
      ).length;
      return workDaysPerWeek + " " + translate(STRINGS.DAYS_OF_WEEK);
    }
    case "Singles": {
      const totalShifts = visit.publicVisitBroadcastDetails.visitInstances.length;
      const shiftOrShifts =
        totalShifts === 1 ? translate(STRINGS.SHIFT) : translate(STRINGS.SHIFTS);

      return `${totalShifts} ${shiftOrShifts}`;
    }
    case "FlexiblePattern": {
      const { flexibleVisitParams, allowMultipleCaregivers } = visit.publicVisitBroadcastDetails;

      const hours = getTotalFlexibleVisitHours(flexibleVisitParams);
      const formattedHours = globalize().formatUnit(hours, "hour");

      return allowMultipleCaregivers
        ? `${translate(STRINGS.UP_TO)} ${formattedHours} ${translate(STRINGS.PER_WEEK)}`
        : `${formattedHours} ${translate(STRINGS.PER_WEEK)}`;
    }
    default:
      return assertNever(visit.publicVisitBroadcastDetails);
  }
}

export function getTaskStatus(task: PatientTaskBroadcastDetails) {
  return task.viewedAt !== null ? "VIEWED" : "NEW";
}

export function getVisitStatus(visit: Visit): VisitStatus {
  if (
    visit.type === "Available" &&
    isDefined(visit.caregiverVisitEngagementDetails.pendingConfirmationUntil)
  ) {
    return "PENDING_CONFIRMATION";
  } else if (visit.caregiverVisitEngagementDetails.requested) {
    return "REQUESTED";
  } else if (visit.caregiverVisitEngagementDetails.moreDetailsViewed) {
    return "VIEWED";
  } else {
    return "NEW";
  }
}

export function getStatusText(visitStatus: VisitStatus): string {
  switch (visitStatus) {
    case "PENDING_CONFIRMATION":
      return translate(STRINGS.PENDING_CONFIRMATION);
    case "REQUESTED":
      return translate(STRINGS.REQUESTED);
    case "VIEWED":
      return translate(STRINGS.VIEWED);
    case "NEW":
      return translate(STRINGS.NEW);
    default:
      return assertNever(visitStatus);
  }
}

export function getStatusColor(visitStatus: VisitStatus): VisitStatusColor {
  switch (visitStatus) {
    case "PENDING_CONFIRMATION":
      return "yellow";
    case "REQUESTED":
      return "green";
    case "VIEWED":
      return "slate";
    case "NEW":
      return "blue";
    default:
      return assertNever(visitStatus);
  }
}

export function getStatusType(
  visitStatus: VisitStatus
): "visit_requested" | "visit_new" | "visit_viewed" | "visit_pending_confirmation" {
  switch (visitStatus) {
    case "PENDING_CONFIRMATION":
      return "visit_pending_confirmation";
    case "REQUESTED":
      return "visit_requested";
    case "VIEWED":
      return "visit_new";
    case "NEW":
      return "visit_viewed";
    default:
      return assertNever(visitStatus);
  }
}
